// skipcq: JS-W1028
import React, { useEffect, useCallback } from 'react'; // skipcq: JS-0128
// skipcq: JS-0257
import { object as pobject, func, bool } from 'prop-types';
// libs
import { Formik, Form, useFormikContext } from 'formik';
import { string, boolean, date, array, object } from 'yup';
import 'yup-phone';
import dayjs from 'dayjs';
// Material UI
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
// components
import DetailsForm from '../overrideBreweryForm';
import SocialsForm from '../socialsForm/SocialsForm';
import AmenitiesForm from '../amenitiesForm/AmenitiesForm';
import { buildInitialAmenityFormValues } from '../amenitiesForm/amenityOverrideUtils';
import { getAmenityTypeKeys } from '../../../../components/amenities/AmenityTypes';

function SaveStateObserver({ onSaveStateChange }) {
  const { isSubmitting, isValid } = useFormikContext();

  useEffect(() => {
    if (!onSaveStateChange) return;
    onSaveStateChange(!isSubmitting && isValid);
  }, [isSubmitting, isValid, onSaveStateChange]);

  return null;
}

SaveStateObserver.propTypes = {
  onSaveStateChange: func,
};

function DetailsFormHandler({
  brewery,
  override = {},
  overrideLoading = false,
  overrideLoadError = null,
  onRetryOverrideLoad,
  handleUpdate,
  onSaveStateChange,
}) {
  const handleSubmit = useCallback((values, formikHelpers) => {
    return handleUpdate(values, formikHelpers);
  }, [handleUpdate]);

  if (!brewery) {
    return null;
  }

  if (overrideLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 240,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (overrideLoadError) {
    return (
      <Box sx={{ minHeight: 240 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load the brewery override from Firestore. Saving is disabled
          until the override can be loaded.
        </Alert>
        {onRetryOverrideLoad ? (
          <Button variant="outlined" onClick={onRetryOverrideLoad}>
            Retry loading override
          </Button>
        ) : null}
      </Box>
    );
  }

  const resolvedOverride = override ?? {};
  const amenityKeys = getAmenityTypeKeys();

  const {
    name = '',
    address1 = '',
    city = '',
    state = '',
    zip = '',
    phone = '',
    isOob = false,
    oobDate = null,
    isTempClosed = false,
    website = '',
    email = '',
    socials = [],
    updateAmenities = false,
    updateSocials = false,
    updateOob = false,
    amenities = {},
  } = resolvedOverride;

  // Convert date strings to Day.js objects for initial values
  // This ensures the DatePicker receives the correct type from the start
  const convertOobDate = (dateValue) => {
    if (!dateValue) return null;
    // If it's already a Day.js object, return it
    if (dayjs.isDayjs(dateValue)) {
      return dateValue;
    }
    // If it's a string, parse it to Day.js (handles formats like "1/1/2026" or ISO strings)
    if (typeof dateValue === 'string') {
      const parsed = dayjs(dateValue);
      return parsed.isValid() ? parsed : null;
    }
    // If it's a Date object, convert it
    if (dateValue instanceof Date) {
      const parsed = dayjs(dateValue);
      return parsed.isValid() ? parsed : null;
    }
    return null;
  };

  // Get the oobDate value (from override or brewery) and convert to Day.js
  const rawOobDate = isOob ? oobDate : brewery.oobDate;
  const convertedOobDate = convertOobDate(rawOobDate);

  const initialValues = {
    name,
    address1,
    city,
    state,
    zip,
    phone,
    isOob: isOob ? isOob : brewery.isOob,
    oobDate: convertedOobDate,
    isTempClosed: isTempClosed ?? brewery.isTempClosed ?? false,
    website,
    email,
    socials: socials && socials.length > 0 ? socials : brewery.socials,
    amenities: buildInitialAmenityFormValues(updateAmenities, amenities, amenityKeys),
    updateAmenities,
    updateSocials,
    updateOob,
  };

  const brewerySchema = object().shape(
    {
      name: string(),
      address1: string(),
      city: string(),
      state: string(),
      zip: string(),
      phone: string().when('phone', {
        is: (value) => value?.length > 0,
        then: string().phone(),
        otherwise: string(),
      }),
      //phone: string().phone(),
      website: string(),
      updateOob: boolean(),
      isOob: boolean(),
      oobDate: date().when('isOob', {
        is: (isOobValue) => isOobValue === true,
        then: date()
          .required('A valid date is required')
          .typeError('A valid date is required'),
        otherwise: date().nullable(),
      }),
      isTempClosed: boolean().when('isOob', {
        is: (isOobValue) => isOobValue === true,
        then: boolean().oneOf(
          [false],
          'A brewery cannot be temporarily and permanently closed at the same time.',
        ),
        otherwise: boolean(),
      }),
      email: string().email(),
      updateAmenities: boolean(),
      updateSocials: boolean(),
      socials: array().when('updateSocials', {
        is: (updateSocialsEnabled) => updateSocialsEnabled === true,
        then: array().of(
          object().shape({
            type: string().oneOf([
              'facebook',
              'twitter',
              'instagram',
              'google',
              'yelp',
              'tripadvisor',
              'other',
            ]),
            link: string().url().required(),
          }),
        ),
        otherwise: array().nullable(),
      }),
    },
    ['phone', 'phone', 'isOob', 'oobDate', 'updateOob', 'updateSocials'],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={brewerySchema}
      onSubmit={handleSubmit}
    >
      {({
        handleBlur,
        handleChange,
        values,
        setFieldTouched,
        setFieldValue,
      }) => {
        return (
            <Container
              maxWidth={false}
              style={{ justifyContent: 'center' }}
              sx={{
                display: 'flex',
              }}
            >
              <Form id="brewery-override-form">
                <SaveStateObserver onSaveStateChange={onSaveStateChange} />
                <DetailsForm
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setFieldTouched={setFieldTouched}
                  setFieldValue={setFieldValue}
                  values={values}
                />
                <SocialsForm values={values} handleChange={handleChange} />
                <AmenitiesForm
                  values={values}
                  setFieldValue={setFieldValue}
                />
              </Form>
            </Container>
        );
      }}
    </Formik>
  );
}

DetailsFormHandler.propTypes = {
  brewery: pobject.isRequired,
  handleUpdate: func.isRequired,
  override: pobject,
  overrideLoading: bool,
  overrideLoadError: pobject,
  onRetryOverrideLoad: func,
  onSaveStateChange: func,
};

export default DetailsFormHandler;
